/**
 * HTTP Service - Base HTTP client with retry logic and error handling
 */

import { API_CONFIG } from '../config/api.config';
import { ApiError } from '../types/api.types';
import type { ApiResponse } from '../types/api.types';

/**
 * Create an ApiError from response
 */
function createApiError(response: Response, data: unknown): ApiError {
  let message = 'Request failed';
  let code: string | undefined;

  if (typeof data === 'object' && data !== null) {
    const apiResponse = data as ApiResponse<unknown>;
    if (apiResponse.error) {
      message = apiResponse.error;
    }
  }

  // Map status codes to error codes
  switch (response.status) {
    case 400:
      code = 'BAD_REQUEST';
      break;
    case 404:
      code = 'NOT_FOUND';
      break;
    case 500:
      code = 'SERVER_ERROR';
      message = 'Server-Problem';
      break;
    case 503:
      code = 'SERVICE_UNAVAILABLE';
      message = 'Service nicht verfügbar';
      break;
  }

  return new ApiError(message, response.status, code);
}

/**
 * Base request function with timeout and retry logic
 */
async function request<T>(
  url: string,
  options: RequestInit = {},
  attempt: number = 1
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    // Parse response
    const data = await response.json() as ApiResponse<T>;

    // Check if request was successful
    if (!response.ok || !data.success) {
      throw createApiError(response, data);
    }

    return data as T;
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle abort (timeout)
    if (error instanceof DOMException && error.name === 'AbortError') {
      const timeoutError = new ApiError(
        'Request timeout',
        503,
        'TIMEOUT'
      );

      // Retry logic
      if (attempt <= API_CONFIG.RETRY_ATTEMPTS) {
        await new Promise(resolve =>
          setTimeout(resolve, API_CONFIG.RETRY_DELAY)
        );
        return request<T>(url, options, attempt + 1);
      }

      throw timeoutError;
    }

    // Handle API errors
    if (error instanceof ApiError) {
      // Retry logic for network errors (status 0 or 503)
      if ((error.status === 0 || error.status === 503) && attempt <= API_CONFIG.RETRY_ATTEMPTS) {
        await new Promise(resolve =>
          setTimeout(resolve, API_CONFIG.RETRY_DELAY)
        );
        return request<T>(url, options, attempt + 1);
      }
      throw error;
    }

    // Handle other errors (network issues)
    const networkError = new ApiError(
      'Netzwerkfehler',
      0,
      'NETWORK_ERROR'
    );

    // Retry logic for network errors
    if (attempt <= API_CONFIG.RETRY_ATTEMPTS) {
      await new Promise(resolve =>
        setTimeout(resolve, API_CONFIG.RETRY_DELAY)
      );
      return request<T>(url, options, attempt + 1);
    }

    throw networkError;
  }
}

/**
 * GET request
 */
export async function get<T>(url: string): Promise<T> {
  return request<T>(url, { method: 'GET' });
}

/**
 * POST request
 */
export async function post<T>(url: string, data: unknown): Promise<T> {
  return request<T>(url, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

/**
 * PUT request
 */
export async function put<T>(url: string, data: unknown): Promise<T> {
  return request<T>(url, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

/**
 * DELETE request
 */
export async function del<T>(url: string): Promise<T> {
  return request<T>(url, { method: 'DELETE' });
}
